import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Mail,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { listSeoReports, refreshSeoReport, type SeoReport, type SeoRow } from "@/lib/gsc.functions";

export const Route = createFileRoute("/_authenticated/admin-seo")({
  component: SeoMonitorPage,
  head: () => ({
    meta: [
      { title: "Beheer — SEO monitoring | Fedor Timmerwerken" },
      {
        name: "description",
        content: "Wekelijkse Search Console-cijfers: CTR, positie, zoektermen en verbeterplan.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const pct = (v: number) => `${(v * 100).toFixed(2)}%`;
const signed = (v: number) => `${v > 0 ? "+" : ""}${v}`;
const pathOf = (url: string) => {
  try {
    return new URL(url).pathname || "/";
  } catch {
    return url;
  }
};

const buttonClass =
  "inline-flex min-h-10 items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50";
const primaryButtonClass =
  "inline-flex min-h-10 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50";

function score(r: SeoRow) {
  return r.clicksDelta * 10 + r.impressionsDelta / 50;
}

function movers(rows: SeoRow[]) {
  const relevant = rows.filter((r) => r.impressions >= 10 || r.prevImpressions >= 10);
  const sorted = [...relevant].sort((a, b) => score(b) - score(a));
  return {
    winners: sorted.filter((r) => score(r) > 0).slice(0, 5),
    losers: sorted
      .filter((r) => score(r) < 0)
      .sort((a, b) => score(a) - score(b))
      .slice(0, 5),
  };
}

function MoversTable({
  title,
  rows,
  label,
  tone,
}: {
  title: string;
  rows: SeoRow[];
  label: (r: SeoRow) => string;
  tone: "up" | "down";
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <h3 className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold">
        {tone === "up" ? (
          <TrendingUp className="h-4 w-4 text-primary" />
        ) : (
          <TrendingDown className="h-4 w-4 text-destructive" />
        )}
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">Geen noemenswaardige wijzigingen.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Item</th>
                <th className="px-3 py-2 text-right font-medium">Klikken</th>
                <th className="px-3 py-2 text-right font-medium">Impressies</th>
                <th className="px-3 py-2 text-right font-medium">CTR</th>
                <th className="px-4 py-2 text-right font-medium">Positie</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-border/70">
                  <td className="px-4 py-2">
                    <span className="break-all">{label(r)}</span>
                    {r.isNew && (
                      <span className="ml-1 text-xs font-medium text-primary">nieuw</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.clicks}{" "}
                    <span className={r.clicksDelta < 0 ? "text-destructive" : "text-primary"}>
                      ({signed(r.clicksDelta)})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.impressions}{" "}
                    <span className={r.impressionsDelta < 0 ? "text-destructive" : "text-primary"}>
                      ({signed(r.impressionsDelta)})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(r.ctr)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {r.position.toFixed(1)}
                    {r.positionDelta !== null && (
                      <span className={r.positionDelta < 0 ? "text-destructive" : "text-primary"}>
                        {" "}
                        ({signed(r.positionDelta)})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeoMonitorPage() {
  const qc = useQueryClient();
  const loadFn = useServerFn(listSeoReports);
  const refreshFn = useServerFn(refreshSeoReport);
  const [message, setMessage] = useState<string | null>(null);

  const reports = useQuery<SeoReport[]>({
    queryKey: ["seo-reports"],
    queryFn: () => loadFn(),
  });

  const refresh = useMutation({
    mutationFn: (sendEmail: boolean) => refreshFn({ data: { sendEmail } }),
    onSuccess: (res: any) => {
      setMessage(
        `Rapport ${res.period} bijgewerkt${res.emailSent ? " en gemaild" : ""}${res.emailError ? ` (mail mislukt: ${res.emailError})` : ""}.`,
      );
      qc.invalidateQueries({ queryKey: ["seo-reports"] });
    },
    onError: (err: any) => setMessage(err?.message ?? "Ophalen mislukt"),
  });

  const latest = reports.data?.[0];
  const pageMovers = latest ? movers(latest.pages) : null;
  const queryMovers = latest ? movers(latest.queries) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/admin"
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Terug naar aanvragen
          </Link>
          <h1 className="font-heading text-3xl font-semibold">SEO monitoring</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Search Console: 28 dagen vs. de 28 dagen daarvoor. Elke maandag automatisch per e-mail.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={buttonClass}
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(false)}
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Nu ophalen
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(true)}
          >
            <Mail className="h-4 w-4" /> Ophalen + mailen
          </button>
        </div>
      </header>

      {message && (
        <p className="mb-6 rounded-md border border-border bg-secondary px-3 py-2 text-sm">
          {message}
        </p>
      )}

      {reports.isLoading && (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Rapporten laden…
        </p>
      )}

      {!reports.isLoading && !latest && (
        <p className="text-sm text-muted-foreground">
          Nog geen rapport opgeslagen. Klik op “Nu ophalen” voor de eerste meting.
        </p>
      )}

      {latest && (
        <div className="space-y-8">
          <section className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {latest.periodStart} t/m {latest.periodEnd} · vorige periode {latest.prevStart} t/m{" "}
              {latest.prevEnd}
            </p>
            <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ["Klikken", `${latest.totals.clicks}`, `was ${latest.prevTotals.clicks}`],
                [
                  "Impressies",
                  `${latest.totals.impressions}`,
                  `was ${latest.prevTotals.impressions}`,
                ],
                ["CTR", pct(latest.totals.ctr), `was ${pct(latest.prevTotals.ctr)}`],
                [
                  "Gem. positie",
                  `${latest.totals.position}`,
                  `was ${latest.prevTotals.position}`,
                ],
              ].map(([label, value, sub]) => (
                <div key={label}>
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="font-heading text-2xl font-semibold tabular-nums">{value}</dd>
                  <p className="text-xs text-muted-foreground">{sub}</p>
                </div>
              ))}
            </dl>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-xl font-semibold">Verbeterplan</h2>
            <ol className="space-y-3">
              {latest.plan.map((item, i) => (
                <li key={i} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{item.titel}</span>
                    <span className="rounded-full border border-border px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                      {item.prioriteit}
                    </span>
                    <span className="text-xs text-muted-foreground">{item.type}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{item.bevinding}</p>
                  <p className="mt-2 text-sm">{item.actie}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-xl font-semibold">Pagina's</h2>
            <MoversTable
              title="Grootste winnaars"
              rows={pageMovers!.winners}
              label={(r) => pathOf(r.key)}
              tone="up"
            />
            <MoversTable
              title="Grootste dalers"
              rows={pageMovers!.losers}
              label={(r) => pathOf(r.key)}
              tone="down"
            />
          </section>

          <section className="space-y-4">
            <h2 className="font-heading text-xl font-semibold">Zoektermen</h2>
            <MoversTable
              title="Grootste winnaars"
              rows={queryMovers!.winners}
              label={(r) => r.key}
              tone="up"
            />
            <MoversTable
              title="Grootste dalers"
              rows={queryMovers!.losers}
              label={(r) => r.key}
              tone="down"
            />
          </section>

          <section>
            <h2 className="mb-3 font-heading text-xl font-semibold">Historie</h2>
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full min-w-[520px] text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Periode</th>
                    <th className="px-3 py-2 text-right font-medium">Klikken</th>
                    <th className="px-3 py-2 text-right font-medium">Impressies</th>
                    <th className="px-3 py-2 text-right font-medium">CTR</th>
                    <th className="px-3 py-2 text-right font-medium">Positie</th>
                    <th className="px-4 py-2 text-right font-medium">Mail</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.data!.map((r) => (
                    <tr key={r.id} className="border-t border-border/70">
                      <td className="px-4 py-2">
                        {r.periodStart} t/m {r.periodEnd}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.totals.clicks}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.totals.impressions}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(r.totals.ctr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.totals.position}</td>
                      <td className="px-4 py-2 text-right">{r.emailSent ? "verzonden" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
